import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  FaTachometerAlt,
  FaBoxes,
  FaFileAlt,
  FaClipboardList,
  FaCheckCircle,
  FaUsers,
  FaChartBar,
  FaChartPie,
  FaCog,
  FaLayerGroup,
  FaTags,
  FaTools,
  FaExclamationTriangle,
  FaShieldAlt,
  FaTasks,
  FaUsersCog,
  FaBuilding,
  FaRoute,
  FaCalendarAlt,
  FaCalendarCheck,
  FaCompass,
  FaQrcode,
} from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin, isManager } from '../../utils/permissions';
import { FooterVersion } from '../common/AppVersion';
import { useLabel } from '../../i18n/useLabel';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  path: string;
  icon: IconType;
  label: string;
  visible: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();

  const mgr = isAdmin(user?.role) || isManager(user?.role);
  const admin = isAdmin(user?.role);
  const superAdmin = user?.role_string === 'super_admin';

  // Resolve platform nouns once per render. Per-tenant overrides flow
  // through DryContext.labels; useLabel falls back to defaults when no
  // override is set. Industry-neutral labels (Dashboard / Calendar /
  // Settings / etc.) stay as literals — they're not platform primitives.
  const labelSites      = useLabel('site',         { form: 'plural' });
  const labelAssetTypes = useLabel('asset_type',   { form: 'plural' });
  const labelAssets     = useLabel('asset',        { form: 'plural' });
  const labelForms      = useLabel('form',         { form: 'plural' });
  const labelSubs       = useLabel('submission',   { form: 'plural' });
  const labelSchedules  = useLabel('schedule',     { form: 'plural' });
  const labelMaint      = useLabel('maintenance',  { form: 'plural' });
  const labelJobs       = useLabel('job_workflow', { form: 'plural' });

  // Five universal workflow groups — industry-agnostic. Customer maps their
  // domain (cleaning / fleet / food-safety / clinical / etc.) to these
  // generic concepts via template packs and tenant-specific terminology
  // overrides (live via the labels above).
  const navGroups: NavGroup[] = [
    {
      title: 'Operate',
      items: [
        { path: '/', icon: FaTachometerAlt, label: 'Dashboard', visible: true },
        { path: '/my-tasks', icon: FaTasks, label: 'My Tasks', visible: true },
        { path: '/scan', icon: FaQrcode, label: 'Scan', visible: true },
        { path: '/calendar', icon: FaCalendarAlt, label: 'Calendar', visible: true },
        { path: '/submissions', icon: FaClipboardList, label: labelSubs, visible: mgr },
        { path: '/approvals', icon: FaCheckCircle, label: 'Approvals', visible: mgr },
        { path: '/flagged', icon: FaExclamationTriangle, label: 'Flagged Checks', visible: mgr },
        { path: '/issues', icon: FaExclamationTriangle, label: 'Issues', visible: true },
      ],
    },
    {
      title: 'Set up',
      items: [
        { path: '/setup', icon: FaCompass, label: 'Setup Hub', visible: mgr },
        { path: '/locations', icon: FaBuilding, label: labelSites, visible: mgr },
        { path: '/asset-types', icon: FaTags, label: labelAssetTypes, visible: admin },
        { path: '/assets', icon: FaBoxes, label: labelAssets, visible: mgr },
        { path: '/forms', icon: FaFileAlt, label: labelForms, visible: mgr },
        { path: '/templates', icon: FaLayerGroup, label: 'Templates', visible: mgr },
        { path: '/schedules', icon: FaUsersCog, label: labelSchedules, visible: mgr },
        { path: '/maintenance', icon: FaTools, label: labelMaint, visible: mgr },
        { path: '/jobs', icon: FaRoute, label: labelJobs, visible: true },
        { path: '/job-schedules', icon: FaCalendarCheck, label: 'Job Schedules', visible: mgr },
        { path: '/qr-codes', icon: FaQrcode, label: 'QR Codes', visible: mgr },
        { path: '/qr-codes/batches', icon: FaQrcode, label: 'QR Batches', visible: admin },
      ],
    },
    {
      title: 'People',
      items: [
        { path: '/users', icon: FaUsers, label: 'Users', visible: admin },
      ],
    },
    {
      title: 'Insights',
      items: [
        { path: '/compliance', icon: FaFileAlt, label: 'Compliance Pack', visible: mgr },
        { path: '/analytics', icon: FaChartBar, label: 'Analytics', visible: mgr },
      ],
    },
    {
      title: 'Reports',
      items: [
        { path: '/reports', icon: FaChartPie, label: 'Reports', visible: mgr },
      ],
    },
    {
      title: 'System',
      items: [
        { path: '/settings', icon: FaCog, label: 'Settings', visible: admin },
        { path: '/admin', icon: FaShieldAlt, label: 'Admin Console', visible: superAdmin },
      ],
    },
  ];

  // Filter out items the user can't see, then drop empty groups so the
  // sidebar doesn't show a "People" header with nothing under it for
  // non-admin viewers.
  const visibleGroups = navGroups
    .map((g) => ({ ...g, items: g.items.filter((i) => i.visible) }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-30 h-full w-64 bg-gray-900 text-white transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'
          } print:hidden`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-3">
                <img src="/logo.svg" alt="Checks & Ticks" className="w-11 h-11 rounded-xl shrink-0" />
                <div>
                  <p className="text-sm font-bold leading-tight">
                    <span className="text-indigo-400">Checks</span>
                    <span className="text-gray-500 font-normal"> &amp; </span>
                    <span className="text-emerald-400">Ticks</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            {visibleGroups.map((group) => (
              <div key={group.title} className="mb-6">
                <h3 className="px-4 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {group.title}
                </h3>
                <ul className="space-y-1">
                  {group.items.map((item) => (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        onClick={onClose}
                        end={item.path === '/'}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${isActive
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                          }`
                        }
                      >
                        <item.icon size={18} aria-hidden="true" />
                        <span>{item.label}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-800">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                &copy; {new Date().getFullYear()} &nbsp;
                <a href="https://thinkbelievedo.com" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  Think Believe Do Ltd.
                </a>
              </p>
              <FooterVersion />
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
